import React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check } from "lucide-react";
import { Select, SelectContent, SelectTrigger } from "@/components/ui/select";

/**
 * Abstract wireframe mockups for the header / footer layout variants + a rich
 * dropdown (WireframeSelect) that renders a tiny SVG mockup next to each name so
 * operators can browse the options visually without clicking through previews.
 *
 * The mockups are intentionally NOT pixel-accurate renders — they're simple
 * grey-rectangle wireframes (logo bar / nav lines / columns / accent chips) that
 * convey the STRUCTURE of each layout at a glance. Zero iframe cost.
 */

const C = {
  bar: "#27272a", // zinc-800 utility-bar backgrounds
  block: "#3f3f46", // zinc-700 solid blocks (logo / ad slot / image)
  line: "#71717a", // zinc-500 nav / text lines
  rule: "#52525b", // zinc-600 hairline rules
  accent: "#f59e0b", // amber-500 CTAs / stripes
};

const VB = "0 0 88 52";

function Frame({ children }) {
  return (
    <svg
      viewBox={VB}
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid meet"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="0.5" y="0.5" width="87" height="51" rx="2.5" fill="none" stroke="#3f3f46" strokeWidth="1" />
      {children}
    </svg>
  );
}

// small helpers ----------------------------------------------------------
const dash = (x, y, w = 6, h = 2.4, fill = C.line) => (
  <rect key={`d${x}-${y}`} x={x} y={y} width={w} height={h} rx="1" fill={fill} />
);
const dot = (x, y, r = 1.5, fill = C.line) => <circle key={`c${x}-${y}`} cx={x} cy={y} r={r} fill={fill} />;
const row = (xs, y, w, h, fill) => xs.map((x) => dash(x, y, w, h, fill));
const dotRow = (xs, y, r, fill) => xs.map((x) => dot(x, y, r, fill));

// HEADER wireframes ------------------------------------------------------
const HEADERS_WF = {
  minimal: () => [
    <rect key="lg" x="6" y="21" width="16" height="9" rx="1" fill={C.block} />,
    ...row([52, 60, 68, 76], 24.5),
  ],
  downtown: () => [
    <rect key="ub" x="6" y="5" width="76" height="4" rx="1" fill={C.bar} />,
    <rect key="lg" x="33" y="13" width="22" height="9" rx="1" fill={C.block} />,
    ...row([26, 36, 46, 56], 30),
    <rect key="cta" x="68" y="28.5" width="12" height="5" rx="2.5" fill={C.accent} />,
  ],
  "urban-centered": () => [
    <rect key="lg" x="28" y="8" width="32" height="11" rx="1" fill={C.block} />,
    <rect key="tg" x="32" y="23" width="24" height="2" rx="1" fill={C.line} />,
    <rect key="p1" x="24" y="32" width="12" height="5" rx="2.5" fill="none" stroke={C.accent} strokeWidth="1" />,
    <rect key="p2" x="38" y="32" width="12" height="5" rx="2.5" fill="none" stroke={C.accent} strokeWidth="1" />,
    <rect key="p3" x="52" y="32" width="12" height="5" rx="2.5" fill="none" stroke={C.accent} strokeWidth="1" />,
  ],
  "news-magazine": () => [
    <rect key="ub" x="6" y="5" width="76" height="4" rx="1" fill={C.bar} />,
    <rect key="lg" x="6" y="13" width="18" height="9" rx="1" fill={C.block} />,
    <rect key="ad" x="48" y="12" width="34" height="11" rx="1" fill={C.block} />,
    <rect key="nb" x="6" y="31" width="76" height="8" rx="1" fill={C.bar} />,
    ...row([10, 20, 30, 40, 50], 34, 6, 2.2),
  ],
  "split-magazine": () => [
    <rect key="lb" x="6" y="7" width="34" height="38" rx="1" fill={C.block} />,
    <rect key="rr" x="44" y="7" width="38" height="38" rx="1" fill={C.accent} opacity="0.32" />,
    ...row([50, 50, 50, 50].map((x, i) => x), 0, 0, 0), // placeholder no-op
    <rect key="n1" x="50" y="14" width="22" height="2.4" rx="1" fill={C.accent} />,
    <rect key="n2" x="50" y="20" width="22" height="2.4" rx="1" fill={C.accent} />,
    <rect key="n3" x="50" y="26" width="22" height="2.4" rx="1" fill={C.accent} />,
    <rect key="n4" x="50" y="32" width="22" height="2.4" rx="1" fill={C.accent} />,
  ],
  "classic-serif": () => [
    <rect key="ub" x="6" y="5" width="76" height="3" rx="1" fill={C.bar} />,
    <rect key="wm" x="24" y="13" width="40" height="9" rx="1" fill={C.block} />,
    <rect key="r1" x="6" y="27" width="76" height="0.9" fill={C.rule} />,
    <rect key="r2" x="6" y="37" width="76" height="0.9" fill={C.rule} />,
    ...row([30, 42, 54], 31, 8, 2.2),
  ],
  "bold-sans-left": () => [
    <rect key="lg" x="6" y="21" width="16" height="9" rx="1" fill={C.block} />,
    ...row([32, 42, 52], 24.5),
    ...dotRow([69, 75, 81], 25.5),
  ],
  "stacked-editorial": () => [
    <rect key="ub" x="6" y="5" width="76" height="4" rx="1" fill={C.bar} />,
    <rect key="dt" x="9" y="6.3" width="12" height="1.6" rx="0.5" fill={C.line} />,
    <rect key="bd" x="38" y="6" width="12" height="2.4" rx="1" fill={C.accent} />,
    ...dotRow([70, 75, 80], 7.1, 1.2),
    <rect key="wm" x="24" y="13" width="40" height="9" rx="1" fill={C.block} />,
    <rect key="pp1" x="38" y="29" width="0.8" height="4" fill={C.rule} />,
    <rect key="pp2" x="52" y="29" width="0.8" height="4" fill={C.rule} />,
    ...row([28, 42, 56], 30, 8, 2.2),
  ],
  "compact-utility": () => [
    <rect key="ub" x="6" y="5" width="76" height="5" rx="1" fill={C.bar} />,
    <rect key="dt" x="9" y="7" width="10" height="1.6" fill={C.line} />,
    ...dotRow([74, 79], 7.8, 1.1),
    <rect key="lg" x="6" y="16" width="16" height="9" rx="1" fill={C.block} />,
    ...row([34, 44, 54], 19.5),
    <rect key="cta" x="68" y="17.5" width="14" height="6" rx="3" fill={C.accent} />,
  ],
  "magazine-classic": () => [
    <rect key="rl" x="8" y="15" width="18" height="0.9" fill={C.rule} />,
    <rect key="wm" x="30" y="10" width="28" height="10" rx="1" fill={C.block} />,
    <rect key="rr" x="62" y="15" width="18" height="0.9" fill={C.rule} />,
    ...row([24, 36, 48, 60], 30, 8, 2.4),
    <rect key="st" x="6" y="39" width="76" height="2" rx="1" fill={C.accent} />,
  ],
};

// FOOTER wireframes ------------------------------------------------------
const col = (x, y0, hw, dw, accentHead = false) => [
  <rect key={`h${x}`} x={x} y={y0} width={hw} height="2.6" rx="1" fill={accentHead ? C.accent : C.line} />,
  <rect key={`a${x}`} x={x} y={y0 + 7} width={dw} height="1.8" fill={C.rule} />,
  <rect key={`b${x}`} x={x} y={y0 + 12} width={dw} height="1.8" fill={C.rule} />,
  <rect key={`c${x}`} x={x} y={y0 + 17} width={dw - 2} height="1.8" fill={C.rule} />,
];

const FOOTERS_WF = {
  minimal: () => [...col(8, 9, 14, 12), ...col(27, 9, 14, 12), ...col(46, 9, 14, 12), ...col(65, 9, 14, 12)],
  "navy-brand": () => [
    <rect key="lg" x="6" y="10" width="26" height="12" rx="1" fill={C.block} />,
    <rect key="ac" x="6" y="25" width="20" height="2" rx="1" fill={C.accent} />,
    <rect key="ln" x="6" y="31" width="24" height="1.6" fill={C.rule} />,
    <rect key="in" x="44" y="14" width="26" height="7" rx="1" fill="none" stroke={C.line} strokeWidth="1" />,
    <rect key="bt" x="72" y="14" width="10" height="7" rx="1" fill={C.accent} />,
    <rect key="cs" x="44" y="26" width="30" height="1.6" fill={C.rule} />,
  ],
  "dark-editorial": () => [
    <rect key="bg" x="4" y="5" width="80" height="42" rx="1.5" fill="#18181b" />,
    ...col(10, 11, 12, 12, true),
    ...col(30, 11, 12, 12, true),
    ...col(50, 11, 12, 12, true),
    ...col(68, 11, 12, 12, true),
  ],
  "centered-magazine": () => [
    <rect key="lg" x="32" y="9" width="24" height="9" rx="1" fill={C.block} />,
    ...row([26, 38, 50], 24, 8, 2.4),
    <rect key="cp" x="30" y="33" width="28" height="1.8" rx="1" fill={C.rule} />,
  ],
  "split-magazine": () => [
    <rect key="bd" x="6" y="6" width="76" height="10" rx="1.5" fill={C.accent} opacity="0.28" />,
    <rect key="in" x="10" y="9" width="34" height="4" rx="1" fill="none" stroke={C.line} strokeWidth="0.8" />,
    <rect key="bt" x="47" y="9" width="12" height="4" rx="1" fill={C.accent} />,
    ...[8, 23, 38, 53, 68].flatMap((x) => [
      <rect key={`h${x}`} x={x} y="22" width="11" height="2.2" rx="1" fill={C.line} />,
      <rect key={`a${x}`} x={x} y="28" width="10" height="1.6" fill={C.rule} />,
      <rect key={`b${x}`} x={x} y="32" width="10" height="1.6" fill={C.rule} />,
    ]),
  ],
  "compact-cols": () => [
    <rect key="lg" x="6" y="10" width="18" height="8" rx="1" fill={C.block} />,
    <rect key="d1" x="6" y="21" width="22" height="1.6" fill={C.rule} />,
    <rect key="d2" x="6" y="25" width="20" height="1.6" fill={C.rule} />,
    ...dotRow([7, 12, 17], 31, 1.2),
    ...col(40, 10, 12, 14, true),
    ...col(64, 10, 12, 14, true),
  ],
  "mono-centered": () => [
    <rect key="wm" x="32" y="10" width="24" height="6" rx="1" fill={C.block} />,
    <rect key="p1" x="37.5" y="21" width="0.8" height="4" fill={C.rule} />,
    <rect key="p2" x="49.5" y="21" width="0.8" height="4" fill={C.rule} />,
    ...row([28, 40, 52], 22, 7, 2),
    <rect key="cp" x="30" y="32" width="28" height="1.6" fill={C.rule} />,
  ],
  "editorial-grid": () => [
    <rect key="wm" x="6" y="9" width="30" height="8" rx="1" fill={C.block} />,
    <rect key="d1" x="6" y="20" width="30" height="1.6" fill={C.rule} />,
    <rect key="d2" x="6" y="24" width="26" height="1.6" fill={C.rule} />,
    ...dotRow([7, 12, 17], 31, 1.2),
    <rect key="dv" x="42" y="8" width="0.8" height="36" fill={C.rule} />,
    ...col(48, 9, 9, 10, true),
    ...col(60, 9, 9, 10, true),
    ...col(72, 9, 9, 10, true),
  ],
  "stripe-band": () => [
    <rect key="st" x="4" y="5" width="80" height="2.4" rx="1" fill={C.accent} />,
    <rect key="lg" x="6" y="14" width="16" height="7" rx="1" fill={C.block} />,
    ...row([34, 44, 54], 17, 7, 2.2),
    ...dotRow([68, 74, 80], 17.5, 1.4),
    <rect key="cp" x="30" y="34" width="28" height="1.8" rx="1" fill={C.rule} />,
  ],
  "big-newsletter": () => [
    <rect key="hb" x="6" y="6" width="76" height="26" rx="2" fill={C.accent} opacity="0.26" />,
    <rect key="hd" x="20" y="11" width="48" height="3" rx="1" fill={C.line} />,
    <rect key="in" x="14" y="19" width="42" height="6" rx="1" fill="none" stroke={C.line} strokeWidth="0.8" />,
    <rect key="bt" x="59" y="19" width="15" height="6" rx="1" fill={C.accent} />,
    ...row([8, 18, 28], 39, 7, 2, C.rule),
    <rect key="cp" x="40" y="38.5" width="18" height="1.8" fill={C.rule} />,
    ...dotRow([74, 80], 40, 1.2),
  ],
};

export function HeaderWireframe({ variant }) {
  const draw = HEADERS_WF[variant] || HEADERS_WF.minimal;
  return <Frame>{draw()}</Frame>;
}

export function FooterWireframe({ variant }) {
  const draw = FOOTERS_WF[variant] || FOOTERS_WF.minimal;
  return <Frame>{draw()}</Frame>;
}

function WireframeThumb({ kind, value, className }) {
  return (
    <span
      className={`inline-block shrink-0 rounded-[3px] bg-zinc-950 border border-zinc-800 overflow-hidden ${className || ""}`}
      aria-hidden="true"
    >
      {kind === "header" ? <HeaderWireframe variant={value} /> : <FooterWireframe variant={value} />}
    </span>
  );
}

// Canonical option lists (value / label / tagline) shared by the Design tab
// dropdowns. Single source of truth for the manual header/footer pickers.
export const HEADER_ITEMS = [
  { value: "minimal", label: "Minimal", tagline: "Simple logo + horizontal nav (default)" },
  { value: "downtown", label: "Downtown", tagline: "Utility bar + big logo + center nav + Subscribe CTA" },
  { value: "urban-centered", label: "Urban Centered", tagline: "Centered mega-logo + tagline + pill nav" },
  { value: "news-magazine", label: "News Magazine", tagline: "Utility bar + logo + ad slot + dark icon nav" },
  { value: "split-magazine", label: "Split Magazine", tagline: "White logo band + blue rail nav" },
  { value: "classic-serif", label: "Classic Serif", tagline: "Utility strip + centered serif wordmark + rail" },
  { value: "bold-sans-left", label: "Bold Sans Left", tagline: "Logo left + inline nav center + socials right" },
  { value: "stacked-editorial", label: "Stacked Editorial", tagline: "NYT-style masthead + pipe-separated nav" },
  { value: "compact-utility", label: "Compact Utility", tagline: "2-row utility bar + logo / nav / subscribe" },
  { value: "magazine-classic", label: "Magazine Classic", tagline: "Rules flank wordmark + accent stripe nav" },
];

export const FOOTER_ITEMS = [
  { value: "minimal", label: "Minimal", tagline: "4-column compact (default)" },
  { value: "navy-brand", label: "Navy Brand", tagline: "Large logo + accent line + newsletter" },
  { value: "dark-editorial", label: "Dark Editorial", tagline: "Picks + popular + category counts" },
  { value: "centered-magazine", label: "Centered Magazine", tagline: "Stacked logo + horizontal nav" },
  { value: "split-magazine", label: "Split Magazine", tagline: "Subscribe band + 5-col grid" },
  { value: "compact-cols", label: "Compact Cols", tagline: "3-col: brand + sections + more" },
  { value: "mono-centered", label: "Mono Centered", tagline: "Mono attribution + pipe nav" },
  { value: "editorial-grid", label: "Editorial Grid", tagline: "Asymmetric brand block + 3-col link rail" },
  { value: "stripe-band", label: "Stripe Band", tagline: "Accent stripe + logo / nav / socials row" },
  { value: "big-newsletter", label: "Big Newsletter", tagline: "Hero subscribe box + thin legal row" },
];

const WireframeItem = React.forwardRef(({ kind, value, label, tagline, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    value={value}
    data-testid={`${kind}-option-${value}`}
    className="relative flex w-full cursor-pointer select-none items-center gap-3 rounded-sm py-1.5 pl-2 pr-8 text-sm text-zinc-200 outline-none focus:bg-zinc-800 data-[state=checked]:bg-zinc-800/60"
    {...props}
  >
    <WireframeThumb kind={kind} value={value} className="w-14 h-8" />
    <span className="min-w-0 flex-1 leading-tight">
      <SelectPrimitive.ItemText>{label}</SelectPrimitive.ItemText>
      {tagline && <span className="block text-[10px] text-zinc-500 truncate">{tagline}</span>}
    </span>
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4 text-amber-400" />
      </SelectPrimitive.ItemIndicator>
    </span>
  </SelectPrimitive.Item>
));
WireframeItem.displayName = "WireframeItem";

/**
 * Rich dropdown showing a tiny SVG wireframe next to each header/footer name.
 * Drop-in replacement for the old native <select> in the Design tab.
 */
export function WireframeSelect({ kind, items, value, onChange, testId }) {
  const current = items.find((i) => i.value === value) || items[0];
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        data-testid={testId}
        className="admin-input bg-transparent border-zinc-800 text-zinc-100 h-auto py-1.5 pl-1.5 pr-3 rounded-sm hover:border-zinc-600 focus:ring-amber-500/40"
      >
        <div className="flex items-center gap-2 min-w-0">
          <WireframeThumb kind={kind} value={current.value} className="w-11 h-6" />
          <span className="truncate text-sm">{current.label}</span>
        </div>
      </SelectTrigger>
      <SelectContent
        data-testid={`${kind}-wireframe-options`}
        className="bg-zinc-900 border-zinc-800 text-zinc-100 max-h-[380px] w-[var(--radix-select-trigger-width)] min-w-[300px]"
      >
        {items.map((it) => (
          <WireframeItem key={it.value} kind={kind} value={it.value} label={it.label} tagline={it.tagline} />
        ))}
      </SelectContent>
    </Select>
  );
}
